import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import TransferModal from "../components/TransferModal";
import CreateAccountModal from "../components/CreateAccountModal";
import { SeedPhraseModal, DeleteConfirmModal, PasswordPromptModal } from "../components/AccountModals";
import { BRAND_ACCENT, labelStyle, inputStyle } from "../utils/constants";
import { truncateAddress, getTierLabel, getErrorMessage, getErrorStatus } from "../utils/formatting";

interface WhitelistEntry {
    address: string;
    label: string;
}

interface AccountAddress {
    index: number;
    address: string;
    sol?: number;
}

interface Account {
    _id: string;
    name: string;
    addresses: AccountAddress[];
    whitelistedAddresses: WhitelistEntry[];
    useWhitelist?: boolean;
    usedByBots?: string[];
}

type WalletAuthAction = "transfer" | "privateKey" | "delete" | "whitelist" | "all";

interface WalletAuthGrant {
    token: string;
    action: WalletAuthAction;
    expiresAt: number;
}

const Accounts: React.FC = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
    const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
    const [inlineError, setInlineError] = useState<string | null>(null);

    // Create account modal
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Seed phrase reveal modal
    const [showSeedModal, setShowSeedModal] = useState(false);
    const [revealedSeed, setRevealedSeed] = useState("");

    // Transfer modal
    const [transferAccount, setTransferAccount] = useState<Account | null>(null);

    // Whitelist management
    const [whitelistEditing, setWhitelistEditing] = useState<string | null>(null);
    const [newWhitelistAddress, setNewWhitelistAddress] = useState("");
    const [newWhitelistLabel, setNewWhitelistLabel] = useState("");
    const [whitelistLoading, setWhitelistLoading] = useState(false);

    // Delete confirmation modal
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    // Wallet action auth tokens (short-lived, scoped per account/action)
    const [walletAuthGrants, setWalletAuthGrants] = useState<Record<string, WalletAuthGrant[]>>({});
    const [passwordPromptFor, setPasswordPromptFor] = useState<{
        accountId: string;
        action: "transfer" | "privateKey" | "delete" | "whitelist";
        addressIndex?: number;
    } | null>(null);

    // Private key reveal
    const [revealedKeys, setRevealedKeys] = useState<Record<string, string>>({});

    const authHeader = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await axios.get("/api/accounts", authHeader());
            setAccounts(res.data.accounts || []);
        } catch (err) {
            console.error("Failed to fetch accounts:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleTransferClose = useCallback(() => setTransferAccount(null), []);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const cacheWalletAuthToken = useCallback(
        (accountId: string, token: string, action: WalletAuthAction, expiresInSeconds = 600) => {
            const expiresAt = Date.now() + Math.max(1, expiresInSeconds) * 1000;
            setWalletAuthGrants((prev) => {
                const current = (prev[accountId] || []).filter((grant) => grant.expiresAt > Date.now());
                const deduped = current.filter((grant) => grant.action !== action);
                return {
                    ...prev,
                    [accountId]: [...deduped, { token, action, expiresAt }],
                };
            });
        },
        [],
    );

    const getWalletAuthToken = useCallback(
        (accountId: string, requiredAction: Exclude<WalletAuthAction, "all">): string | null => {
            const grants = walletAuthGrants[accountId] || [];
            const now = Date.now();
            const matching = grants.find(
                (grant) =>
                    grant.expiresAt > now
                    && (grant.action === "all" || grant.action === requiredAction),
            );
            return matching?.token || null;
        },
        [walletAuthGrants],
    );

    const handleTransferWalletAuthIssued = useCallback(
        (accountId: string, walletAuthToken: string, action: WalletAuthAction, expiresIn?: number) => {
            cacheWalletAuthToken(accountId, walletAuthToken, action, expiresIn);
        },
        [cacheWalletAuthToken],
    );

    const copyAddress = (address: string) => {
        navigator.clipboard.writeText(address);
        setCopiedAddress(address);
        setTimeout(() => setCopiedAddress(null), 2000);
    };

    // Callback after account is created
    const handleAccountCreated = useCallback((mnemonic?: string) => {
        if (mnemonic) {
            setRevealedSeed(mnemonic);
            setShowSeedModal(true);
        }
        fetchAccounts();
    }, [fetchAccounts]);

    // Delete account (password -> short-lived auth token)
    // Throws on error so DeleteConfirmModal can display it
    const handleDelete = async (id: string, password: string) => {
        const verify = await axios.post(
            `/api/accounts/${id}/verify-password`,
            { walletPassword: password, action: "delete" },
            authHeader(),
        );
        const walletAuthToken = verify.data?.walletAuthToken;
        if (!walletAuthToken) {
            throw new Error("Wallet verification did not return an auth token");
        }
        cacheWalletAuthToken(id, walletAuthToken, "delete", verify.data?.expiresIn);
        await axios.delete(`/api/accounts/${id}`, {
            ...authHeader(),
            data: { walletAuthToken },
        });
        setAccounts((prev) => prev.filter((a) => a._id !== id));
        setDeleteConfirmId(null);
        setWalletAuthGrants((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    // Whitelist management
    const handleAddWhitelist = async (accountId: string) => {
        if (!newWhitelistAddress.trim() || !newWhitelistLabel.trim()) return;
        const walletAuthToken = getWalletAuthToken(accountId, "whitelist");
        if (!walletAuthToken) {
            setPasswordPromptFor({ accountId, action: "whitelist" });
            return;
        }
        setWhitelistLoading(true);
        try {
            await axios.patch(
                `/api/accounts/${accountId}/whitelist`,
                {
                    action: "add",
                    address: newWhitelistAddress.trim(),
                    label: newWhitelistLabel.trim(),
                    walletAuthToken,
                },
                authHeader()
            );
            setNewWhitelistAddress("");
            setNewWhitelistLabel("");
            fetchAccounts();
        } catch (err: unknown) {
            if (getErrorStatus(err) === 403) {
                setPasswordPromptFor({ accountId, action: "whitelist" });
                return;
            }
            setInlineError(getErrorMessage(err, "Failed to add address"));
        } finally {
            setWhitelistLoading(false);
        }
    };

    const handleRemoveWhitelist = async (
        accountId: string,
        address: string
    ) => {
        const walletAuthToken = getWalletAuthToken(accountId, "whitelist");
        if (!walletAuthToken) {
            setPasswordPromptFor({ accountId, action: "whitelist" });
            return;
        }
        try {
            await axios.patch(
                `/api/accounts/${accountId}/whitelist`,
                { action: "remove", address, walletAuthToken },
                authHeader()
            );
            fetchAccounts();
        } catch (err: unknown) {
            if (getErrorStatus(err) === 403) {
                setPasswordPromptFor({ accountId, action: "whitelist" });
                return;
            }
            setInlineError(getErrorMessage(err, "Failed to remove address"));
        }
    };

    // Password verification (called from PasswordPromptModal)
    // Throws on error so the modal can display it
    const handleVerifyPassword = async (password: string) => {
        if (!passwordPromptFor) return;
        const verify = await axios.post(
            `/api/accounts/${passwordPromptFor.accountId}/verify-password`,
            { walletPassword: password, action: passwordPromptFor.action },
            authHeader()
        );
        const walletAuthToken = verify.data?.walletAuthToken as string | undefined;
        if (!walletAuthToken) {
            throw new Error("Wallet verification did not return an auth token");
        }

        const action = passwordPromptFor.action;
        const accountId = passwordPromptFor.accountId;
        const addressIndex = passwordPromptFor.addressIndex;
        cacheWalletAuthToken(accountId, walletAuthToken, action, verify.data?.expiresIn);

        setPasswordPromptFor(null);

        // Proceed with the original action
        if (action === "transfer") {
            const account = accounts.find((a) => a._id === accountId);
            if (account) setTransferAccount(account);
        } else if (action === "privateKey" && addressIndex !== undefined) {
            fetchPrivateKey(accountId, addressIndex, walletAuthToken);
        } else if (action === "whitelist") {
            setWhitelistEditing(accountId);
        }
    };

    // Private key export
    const fetchPrivateKey = async (accountId: string, index: number, walletAuthToken: string) => {
        try {
            const res = await axios.post(
                `/api/accounts/${accountId}/private-key`,
                { walletAuthToken, addressIndex: index },
                authHeader()
            );
            const key = `${accountId}-${index}`;
            setRevealedKeys((prev) => ({ ...prev, [key]: res.data.privateKey }));
            // Auto-hide after 30 seconds
            setTimeout(() => {
                setRevealedKeys((prev) => {
                    const updated = { ...prev };
                    delete updated[key];
                    return updated;
                });
            }, 30000);
        } catch (err: unknown) {
            if (getErrorStatus(err) === 403) {
                setPasswordPromptFor({ accountId, action: "privateKey", addressIndex: index });
                return;
            }
            setInlineError(getErrorMessage(err, "Failed to export private key"));
        }
    };

    const handleShowKey = (accountId: string, index: number) => {
        const key = `${accountId}-${index}`;
        if (revealedKeys[key]) {
            setRevealedKeys((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            return;
        }
        const walletAuthToken = getWalletAuthToken(accountId, "privateKey");
        if (walletAuthToken) {
            fetchPrivateKey(accountId, index, walletAuthToken);
        } else {
            setPasswordPromptFor({ accountId, action: "privateKey", addressIndex: index });
        }
    };

    return (
        <div
            className="sovereign-page-shell"
            style={{
                minHeight: "100vh",
                color: "white",
            }}
        >
            {/* Header */}
            <header
                className="app-header"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "12px 16px",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    background: "rgba(0,0,0,0.3)",
                    backdropFilter: "blur(10px)",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                    gap: "8px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {/* Back arrow */}
                        <button
                            onClick={() => navigate("/dashboard")}
                            style={{
                                background: "none",
                                border: "none",
                                color: "white",
                                cursor: "pointer",
                                fontSize: "22px",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                            }}
                            title="Back to Dashboard"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="sovereign-brand" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <img src="/logo1.png" alt="Stellaris" style={{ width: '28px', height: '28px', opacity: 0.9 }} />
                            <h1 className="sovereign-wordmark" style={{ fontSize: '24px', fontWeight: 600, color: '#f0f0f0', margin: 0, letterSpacing: '-0.02em', lineHeight: 1 }}>
                                Stellaris
                            </h1>
                        </div>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        {/* Badges (non-clickable) */}
                        <div
                            className="sovereign-user-chip"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                cursor: "default",
                            }}
                        >
                            <span style={{ color: "#6b7280" }}>@</span>
                            {user?.username}
                        </div>
                        {user?.tier === "pro" ? (
                            <div className="sovereign-tier-chip sovereign-tier-chip--pro" style={{ cursor: "default" }}>
                                Pro
                            </div>
                        ) : (
                            <div className="sovereign-tier-chip" style={{ cursor: "default" }}>
                                {getTierLabel(user?.tokenBalance)}
                            </div>
                        )}

                        {/* Separator */}
                        <div className="sovereign-separator" />

                        {/* Nav buttons (clickable) */}
                        <button
                            onClick={() => navigate("/dashboard")}
                            className="sovereign-nav-button sovereign-nav-button--accent"
                            style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                        >
                            Dashboard →
                        </button>
                        {user?.isAdmin && (
                            <button
                                onClick={() => navigate("/admin")}
                                className="sovereign-nav-button sovereign-nav-button--data"
                                style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                            >
                                Admin →
                            </button>
                        )}
                        <button
                            onClick={logout}
                            className="sovereign-nav-button sovereign-nav-button--danger"
                            style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                        >
                            Logout →
                        </button>
                    </div>
                </div>
            </header>

            {/* Main content */}
            <div className="sovereign-content-shell" style={{ maxWidth: "960px", margin: "0 auto", padding: "24px 16px" }}>
                <div
                    className="sovereign-page-header"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                    }}
                >
                    <h2 className="sovereign-page-title" style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
                        Accounts
                    </h2>
                    <button
                        className="btn-primary"
                        onClick={() => setShowCreateModal(true)}
                        style={{ padding: "10px 20px", fontSize: "14px" }}
                    >
                        Create Account
                    </button>
                </div>

                {inlineError && (
                    <div style={{
                        padding: "10px 16px",
                        marginBottom: "12px",
                        borderRadius: "8px",
                        background: "rgba(239,68,68,0.15)",
                        border: "1px solid rgba(239,68,68,0.3)",
                        color: "#f87171",
                        fontSize: "13px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}>
                        <span>{inlineError}</span>
                        <button
                            onClick={() => setInlineError(null)}
                            style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: "16px", padding: "0 4px" }}
                        >
                            &times;
                        </button>
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                        Loading accounts...
                    </div>
                ) : accounts.length === 0 ? (
                    <div
                        className="glass-card"
                        style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#6b7280",
                        }}
                    >
                        No accounts yet. Create one to get started.
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        {accounts.map((account) => {
                            const isExpanded = expandedAccount === account._id;
                            const fundingAddr = account.addresses?.find(
                                (a) => a.index === 0
                            );
                            const usedByBots = account.usedByBots?.length ?? 0;

                            return (
                                <div
                                    key={account._id}
                                    className="glass-card"
                                    style={{ padding: "20px" }}
                                >
                                    {/* Account header */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            flexWrap: "wrap",
                                            gap: "12px",
                                        }}
                                    >
                                        <div style={{ flex: 1, minWidth: "200px" }}>
                                            <h3
                                                style={{
                                                    fontSize: "18px",
                                                    fontWeight: "bold",
                                                    margin: "0 0 6px 0",
                                                }}
                                            >
                                                {account.name}
                                            </h3>
                                            {fundingAddr && (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: "13px",
                                                            color: "#9ca3af",
                                                            fontFamily: "monospace",
                                                        }}
                                                    >
                                                        {truncateAddress(fundingAddr.address)}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            copyAddress(fundingAddr.address)
                                                        }
                                                        style={{
                                                            padding: "2px 6px",
                                                            borderRadius: "4px",
                                                            background: "rgba(255,255,255,0.1)",
                                                            border: "none",
                                                            color:
                                                                copiedAddress ===
                                                                fundingAddr.address
                                                                    ? BRAND_ACCENT
                                                                    : "#9ca3af",
                                                            cursor: "pointer",
                                                            fontSize: "11px",
                                                        }}
                                                    >
                                                        {copiedAddress === fundingAddr.address
                                                            ? "Copied"
                                                            : "Copy"}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {/* Secondary actions (left) */}
                                            <div style={{ display: "flex", gap: "6px" }}>
                                                <button
                                                    className="btn-ghost"
                                                    onClick={() =>
                                                        setExpandedAccount(
                                                            isExpanded ? null : account._id
                                                        )
                                                    }
                                                >
                                                    {isExpanded ? "Collapse" : "Expand"}
                                                </button>
                                                {account.useWhitelist !== false && (
                                                    <button
                                                        className="btn-ghost"
                                                        onClick={() => {
                                                            if (whitelistEditing === account._id) {
                                                                setWhitelistEditing(null);
                                                            } else if (getWalletAuthToken(account._id, "whitelist")) {
                                                                setWhitelistEditing(account._id);
                                                            } else {
                                                                setPasswordPromptFor({ accountId: account._id, action: "whitelist" });
                                                            }
                                                        }}
                                                    >
                                                        Whitelist
                                                    </button>
                                                )}
                                            </div>
                                            {/* Primary actions (right) */}
                                            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                                <button
                                                    onClick={() => setTransferAccount(account)}
                                                    style={{
                                                        padding: "7px 16px",
                                                        borderRadius: "8px",
                                                        background: "linear-gradient(135deg, rgba(153,69,255,0.25), rgba(20,241,149,0.15))",
                                                        border: "1px solid rgba(153,69,255,0.4)",
                                                        color: "#c084fc",
                                                        cursor: "pointer",
                                                        fontSize: "12px",
                                                        fontWeight: 600,
                                                        transition: "all 0.2s ease",
                                                    }}
                                                >
                                                    Transfer
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setDeleteConfirmId(account._id);
                                                    }}
                                                    disabled={usedByBots > 0}
                                                    style={{
                                                        padding: "6px 10px",
                                                        borderRadius: "6px",
                                                        background: "rgba(239,68,68,0.1)",
                                                        border: "1px solid rgba(239,68,68,0.2)",
                                                        color: "#ef4444",
                                                        cursor: usedByBots > 0 ? "not-allowed" : "pointer",
                                                        fontSize: "11px",
                                                        opacity: usedByBots > 0 ? 0.4 : 1,
                                                        transition: "all 0.2s ease",
                                                    }}
                                                    title={
                                                        usedByBots > 0
                                                            ? `Used by ${usedByBots} bot(s)`
                                                            : "Delete account"
                                                    }
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded addresses */}
                                    {isExpanded && (
                                        <div
                                            style={{
                                                marginTop: "16px",
                                                borderTop:
                                                    "1px solid rgba(255,255,255,0.1)",
                                                paddingTop: "12px",
                                            }}
                                        >
                                            <label style={labelStyle}>
                                                Derived Addresses
                                            </label>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "6px",
                                                }}
                                            >
                                                {account.addresses.map((a) => {
                                                    const keyId = `${account._id}-${a.index}`;
                                                    const revealedKey = revealedKeys[keyId];
                                                    return (
                                                        <div key={a.index}>
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent:
                                                                        "space-between",
                                                                    alignItems: "center",
                                                                    padding: "8px 12px",
                                                                    borderRadius: "6px",
                                                                    background:
                                                                        "rgba(255,255,255,0.05)",
                                                                    fontSize: "13px",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "8px",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            color: "#6b7280",
                                                                            minWidth: "24px",
                                                                        }}
                                                                    >
                                                                        {a.index}
                                                                    </span>
                                                                    <span
                                                                        style={{
                                                                            fontFamily: "monospace",
                                                                            color: "#d1d5db",
                                                                        }}
                                                                    >
                                                                        {truncateAddress(a.address)}
                                                                    </span>
                                                                    <button
                                                                        onClick={() =>
                                                                            copyAddress(a.address)
                                                                        }
                                                                        style={{
                                                                            padding: "2px 6px",
                                                                            borderRadius: "4px",
                                                                            background:
                                                                                "rgba(255,255,255,0.1)",
                                                                            border: "none",
                                                                            color:
                                                                                copiedAddress ===
                                                                                a.address
                                                                                    ? BRAND_ACCENT
                                                                                    : "#6b7280",
                                                                            cursor: "pointer",
                                                                            fontSize: "11px",
                                                                        }}
                                                                    >
                                                                        {copiedAddress === a.address
                                                                            ? "Copied"
                                                                            : "Copy"}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleShowKey(account._id, a.index)}
                                                                        style={{
                                                                            padding: "2px 6px",
                                                                            borderRadius: "4px",
                                                                            background: revealedKey
                                                                                ? "rgba(239,68,68,0.15)"
                                                                                : "rgba(255,255,255,0.1)",
                                                                            border: "none",
                                                                            color: revealedKey ? "#ef4444" : "#6b7280",
                                                                            cursor: "pointer",
                                                                            fontSize: "11px",
                                                                        }}
                                                                    >
                                                                        {revealedKey ? "Hide Key" : "Show Key"}
                                                                    </button>
                                                                </div>
                                                                <span
                                                                    style={{
                                                                        color: BRAND_ACCENT,
                                                                        fontFamily: "monospace",
                                                                    }}
                                                                >
                                                                    {a.sol !== undefined
                                                                        ? `${a.sol.toFixed(4)} SOL`
                                                                        : "--"}
                                                                </span>
                                                            </div>
                                                            {/* Revealed private key */}
                                                            {revealedKey && (
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "8px",
                                                                        padding: "6px 12px",
                                                                        marginTop: "2px",
                                                                        borderRadius: "0 0 6px 6px",
                                                                        background: "rgba(239,68,68,0.08)",
                                                                        border: "1px solid rgba(239,68,68,0.2)",
                                                                        borderTop: "none",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontFamily: "monospace",
                                                                            fontSize: "11px",
                                                                            color: "#d1d5db",
                                                                            wordBreak: "break-all",
                                                                            flex: 1,
                                                                        }}
                                                                    >
                                                                        {revealedKey}
                                                                    </span>
                                                                    <button
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(revealedKey);
                                                                            setCopiedAddress(keyId);
                                                                            setTimeout(() => setCopiedAddress(null), 2000);
                                                                        }}
                                                                        style={{
                                                                            padding: "2px 8px",
                                                                            borderRadius: "4px",
                                                                            background: "rgba(255,255,255,0.1)",
                                                                            border: "none",
                                                                            color: copiedAddress === keyId ? BRAND_ACCENT : "#9ca3af",
                                                                            cursor: "pointer",
                                                                            fontSize: "11px",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {copiedAddress === keyId ? "Copied" : "Copy Key"}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            setRevealedKeys((prev) => {
                                                                                const updated = { ...prev };
                                                                                delete updated[keyId];
                                                                                return updated;
                                                                            });
                                                                        }}
                                                                        style={{
                                                                            padding: "2px 6px",
                                                                            borderRadius: "4px",
                                                                            background: "rgba(239,68,68,0.15)",
                                                                            border: "none",
                                                                            color: "#ef4444",
                                                                            cursor: "pointer",
                                                                            fontSize: "11px",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        Hide
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Whitelist management */}
                                    {whitelistEditing === account._id && (
                                        <div
                                            style={{
                                                marginTop: "16px",
                                                borderTop:
                                                    "1px solid rgba(255,255,255,0.1)",
                                                paddingTop: "12px",
                                            }}
                                        >
                                            <label style={labelStyle}>
                                                Whitelisted Addresses
                                            </label>
                                            {account.whitelistedAddresses.length === 0 ? (
                                                <p
                                                    style={{
                                                        color: "#6b7280",
                                                        fontSize: "13px",
                                                        marginBottom: "12px",
                                                    }}
                                                >
                                                    No whitelisted addresses.
                                                </p>
                                            ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "6px",
                                                        marginBottom: "12px",
                                                    }}
                                                >
                                                    {account.whitelistedAddresses.map((w) => (
                                                        <div
                                                            key={w.address}
                                                            style={{
                                                                display: "flex",
                                                                justifyContent:
                                                                    "space-between",
                                                                alignItems: "center",
                                                                padding: "6px 10px",
                                                                borderRadius: "6px",
                                                                background:
                                                                    "rgba(255,255,255,0.05)",
                                                                fontSize: "13px",
                                                            }}
                                                        >
                                                            <div>
                                                                <span
                                                                    style={{
                                                                        color: "white",
                                                                        marginRight: "8px",
                                                                    }}
                                                                >
                                                                    {w.label}
                                                                </span>
                                                                <span
                                                                    style={{
                                                                        color: "#6b7280",
                                                                        fontFamily: "monospace",
                                                                    }}
                                                                >
                                                                    {truncateAddress(w.address)}
                                                                </span>
                                                            </div>
                                                            <button
                                                                onClick={() =>
                                                                    handleRemoveWhitelist(
                                                                        account._id,
                                                                        w.address
                                                                    )
                                                                }
                                                                style={{
                                                                    padding: "2px 8px",
                                                                    borderRadius: "4px",
                                                                    background:
                                                                        "rgba(239,68,68,0.15)",
                                                                    border: "none",
                                                                    color: "#ef4444",
                                                                    cursor: "pointer",
                                                                    fontSize: "11px",
                                                                }}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Add new whitelist entry */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "8px",
                                                    alignItems: "flex-end",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <div style={{ flex: 1, minWidth: "140px" }}>
                                                    <label style={labelStyle}>Label</label>
                                                    <input
                                                        className="input-dark"
                                                        style={{ ...inputStyle, width: "100%" }}
                                                        placeholder="e.g. Main Wallet"
                                                        value={newWhitelistLabel}
                                                        onChange={(e) =>
                                                            setNewWhitelistLabel(e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div style={{ flex: 2, minWidth: "200px" }}>
                                                    <label style={labelStyle}>Address</label>
                                                    <input
                                                        className="input-dark"
                                                        style={{ ...inputStyle, width: "100%" }}
                                                        placeholder="Solana address"
                                                        value={newWhitelistAddress}
                                                        onChange={(e) =>
                                                            setNewWhitelistAddress(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <button
                                                    className="btn-primary"
                                                    onClick={() =>
                                                        handleAddWhitelist(account._id)
                                                    }
                                                    disabled={
                                                        whitelistLoading ||
                                                        !newWhitelistAddress.trim() ||
                                                        !newWhitelistLabel.trim()
                                                    }
                                                    style={{
                                                        padding: "10px 16px",
                                                        fontSize: "13px",
                                                        opacity:
                                                            !newWhitelistAddress.trim() ||
                                                            !newWhitelistLabel.trim()
                                                                ? 0.5
                                                                : 1,
                                                    }}
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create Account Modal */}
            <CreateAccountModal
                show={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={handleAccountCreated}
                authHeader={authHeader}
            />

            {/* Seed Phrase Reveal Modal */}
            <SeedPhraseModal
                show={showSeedModal}
                seed={revealedSeed}
                onClose={() => {
                    setShowSeedModal(false);
                    setRevealedSeed("");
                }}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                accountId={deleteConfirmId}
                onClose={() => setDeleteConfirmId(null)}
                onDelete={handleDelete}
            />

            {/* Password Prompt Modal */}
            <PasswordPromptModal
                promptFor={passwordPromptFor}
                onClose={() => setPasswordPromptFor(null)}
                onVerify={handleVerifyPassword}
            />

            {/* Transfer Modal */}
            {transferAccount && (
                <TransferModal
                    accountId={transferAccount._id}
                    addresses={transferAccount.addresses}
                    whitelistedAddresses={transferAccount.whitelistedAddresses}
                    useWhitelist={transferAccount.useWhitelist !== false}
                    walletAuthToken={getWalletAuthToken(transferAccount._id, "transfer") || undefined}
                    onWalletAuthIssued={(walletAuthToken, action, expiresIn) =>
                        handleTransferWalletAuthIssued(transferAccount._id, walletAuthToken, action, expiresIn)
                    }
                    onClose={handleTransferClose}
                />
            )}
        </div>
    );
};

export default Accounts;
